import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Container,
  MenuItem,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
  Typography,
} from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Cancel from "@mui/icons-material/Cancel";
import axiosInstance from "../utils/axiosInstance";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { logger } from "../utils/logger";
import SnackbarAlert from "./SnackBarAlert";

// Only superadmin and admin roles allowed
const roles = ["admin1", "admin2", "editor1", "editor2"];

const RegisterForm = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("editor");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openForm, setOpenForm] = useState(true);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const passwordRules = {
    minLength: password.length >= 6,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  useEffect(() => {
    logger.info("Register form opened");
    return () => {
      logger.info("Register form closed");
    };
  }, []);

  const formValidation = () => {
    if (!username || !email || !password) {
      setErrorMessage("All fields are required.");
      logger.warn("Validation failed: missing fields", {
        username,
        email,
        role,
      });
      return false;
    }
    if (
      !passwordRules.minLength ||
      !passwordRules.hasUppercase ||
      !passwordRules.hasLowercase ||
      !passwordRules.hasNumber ||
      !passwordRules.hasSpecialChar
    ) {
      setErrorMessage("Password does not meet all requirements.");
      logger.warn("Validation failed: password requirements not met", {
        username,
        email,
        role,
      });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email)) {
      setErrorMessage("Email is not valid.");
      logger.warn("Validation failed: invalid email format", {
        username,
        email,
        role,
      });
      return false;
    }
    return true;
  };
  const handleSubmit = async (e) => {
    formValidation();
    if (!formValidation()) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    try {
      await axiosInstance.post("/api/auth/register", {
        username,
        email,
        password,
        role,
      });
      setFeedback({
        open: true,
        message: "User registered successfully!",
        severity: "success",
      });
      logger.info("User registered successfully", {
        username,
        email,
        role,
      });
      // Reset form
      setUsername("");
      setEmail("");
      setPassword("");
      setRole("editor");
    } catch (error) {
      setFeedback({
        open: true,
        message: "Error creating user. Please try again.",
        severity: "error",
      });
      if (error.response?.data?.message) {
        var errMsg = error.response.data.message;
      }

      logger.error("User registration failed", {
        username,
        email,
        role,
        error: errMsg,
      });
    }
  };

  const handleCloseSnackbar = () => {
    setErrorMessage("");
  };

  const renderChecklistItem = (condition, text) => (
    <ListItem>
      <ListItemIcon>
        {condition ? <CheckCircle color="success" /> : <Cancel color="error" />}
      </ListItemIcon>
      <ListItemText primary={text} />
    </ListItem>
  );

  return (
    <Container maxWidth="sm">
      <form
        open={() => setOpenForm(true)}
        onSubmit={handleSubmit}
        onClose={() => setOpenForm(false)}
      >
        <Box mb={2}>
          <TextField
            label="Username"
            variant="outlined"
            fullWidth
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
          />
        </Box>
        <Box mb={2}>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Box>
        <Box mb={2}>
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    data-log="Show/Hide Password Icon Button"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box mb={2}>
          <TextField
            select
            label="Role"
            variant="outlined"
            fullWidth
            required
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {roles.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Password checklist */}
        <Box mb={2} p={1} border="1px solid #ccc" borderRadius={2}>
          <Typography variant="subtitle2" fontWeight="bold">
            Password must contain:
          </Typography>
          <List dense>
            {renderChecklistItem(
              passwordRules.minLength,
              "At least 6 characters"
            )}
            {renderChecklistItem(
              passwordRules.hasUppercase,
              "An uppercase letter (A-Z)"
            )}
            {renderChecklistItem(
              passwordRules.hasLowercase,
              "A lowercase letter (a-z)"
            )}
            {renderChecklistItem(passwordRules.hasNumber, "A number (0-9)")}
            {renderChecklistItem(
              passwordRules.hasSpecialChar,
              "A special character (!@#$...)"
            )}
          </List>
        </Box>

        <Button type="submit" variant="contained" color="primary" fullWidth>
          Register
        </Button>
      </form>

      {/* Success / Error Snackbars */}
      <SnackbarAlert feedback={feedback} onClose={handleCloseSnackbar} />
    </Container>
  );
};

export default RegisterForm;
